import { getValidTranslations } from "./translation_helpers";

/**
 * Formats form fields for API submission.
 * Filters out field translations that don't have a valid label,
 * ensuring only properly filled fields are submitted.
 * 
 * @param {Array} fields - Array of form fields from FormEditor
 * @returns {Array} - Formatted fields ready for API submission
 */
export const formatFormFields = (fields) => {
    if (!fields || !Array.isArray(fields)) {
        return [];
    }

    return fields.map((field, index) => {
        const formattedField = {
            id: field.id || null,
            translations: getValidTranslations(field.translations, ['label']),
            field_type: field.field_type,
            datatype: field.datatype || 'text',
            required: field.required || false,
            ui_order: index,
        };

        // Add options for radio/checkbox fields (field_type 3 or 4)
        if (field.field_type === 3 || field.field_type === 4) {
            formattedField.options = (field.options || []).map((option, optionIndex) => ({
                id: option.id || null,
                translations: getValidTranslations(option.translations, ['label']),
                ui_order: optionIndex
            }));
        }

        return formattedField;
    });
};

/**
 * Formats the complete form object for API submission.
 * Applies field formatting and handles date fields.
 * 
 * @param {Object} form - Form object from FormEditor
 * @param {Object} options - Additional options
 * @param {boolean} options.fieldsOnly - When true, sets date fields to null (for feedback forms)
 * @returns {Object} - Formatted form ready for API submission
 */
export const formatFormPayload = (form, options = {}) => {
    if (!form) {
        return null;
    }

    const { fieldsOnly = false } = options;

    return {
        id: form.id || null,
        release_date: fieldsOnly ? null : form.release_date,
        expire_date: fieldsOnly ? null : form.expire_date,
        anonymous: fieldsOnly ? false : (form.anonymous !== undefined ? form.anonymous : true),
        allow_multiple_submissions: fieldsOnly ? true : (form.allow_multiple_submissions || false),
        fields: formatFormFields(form.fields || []),
    };
};

/**
 * Validates form fields and returns an array of validation errors.
 * Used for UI validation before submission.
 * 
 * @param {Array} fields - Array of form fields
 * @param {Array} languages - Array of language objects with 'code' property
 * @param {Object} i18n - i18n instance for translations
 * @returns {Array} - Array of error objects { fieldIndex, identifier, message }
 */
export const validateFormFields = (fields, languages, i18n) => {
    const errors = [];

    if (!fields || !Array.isArray(fields)) {
        return errors;
    }

    fields.forEach((field, index) => {
        const fieldIdentifier = getFieldIdentifier(field, languages, index, i18n);

        // Check that at least one language has a label
        let hasFieldLabel = false;
        languages.forEach((lang) => {
            if (field.translations[lang.code] && field.translations[lang.code].label) {
                hasFieldLabel = true;
            }
        });

        if (!hasFieldLabel) {
            errors.push({
                fieldIndex: index,
                identifier: fieldIdentifier,
                message: i18n.t("otsikko puuttuu kaikilla kielillä"),
            });
        }

        // Check options for radio/checkbox fields (field_type 3 or 4)
        if (field.field_type === 3 || field.field_type === 4) {
            if (!field.options || field.options.length === 0) {
                errors.push({
                    fieldIndex: index,
                    identifier: fieldIdentifier,
                    message: i18n.t("tulee olla vähintään yksi vastausvaihtoehto"),
                });
            } else {
                field.options.forEach((option, optIndex) => {
                    let hasOptionLabel = false;
                    languages.forEach((lang) => {
                        if (option.translations[lang.code] && option.translations[lang.code].label) {
                            hasOptionLabel = true;
                        }
                    });
                    if (!hasOptionLabel) {
                        errors.push({
                            fieldIndex: index,
                            identifier: fieldIdentifier,
                            message: `${i18n.t("vaihtoehto")} ${optIndex + 1} ${i18n.t("puuttuu kaikilla kielillä")}`,
                        });
                    }
                });
            }
        }
    });

    return errors;
};

/**
 * Gets a human-readable identifier for a field (label or field number).
 * 
 * @param {Object} field - The field object
 * @param {Array} languages - Array of language objects with 'code' property
 * @param {number} index - Field index in the array
 * @param {Object} i18n - i18n instance for translations
 * @returns {string} - Field identifier (quoted label or "Kentän X")
 */
const getFieldIdentifier = (field, languages, index, i18n) => {
    // Try to get a field label from any language
    for (const lang of languages) {
        if (field.translations[lang.code] && field.translations[lang.code].label) {
            return `"${field.translations[lang.code].label}"`;
        }
    }
    // Fallback to field number if no label found
    return i18n.t("Kentän") + " " + (index + 1);
};
